import { useEffect, useState, useRef } from "react";
import SideDrawerOverlay from "../../SideOverLay";
import { Button } from "../../../common/components/Button";
import { Button as MuiButton } from "../../components/Common/Button";
import styles from "./index.module.scss";
import CheckBoxChecked from "../../../assets/images/checkBoxChecked.svg";
import CheckBoxUnchecked from "../../../assets/images/checkboxUnchecked.svg";
import RadioUnchecked from "../../../assets/images/radioUncheck.svg";
import MultiSelect from "../../../pages/SearchProp";
import dropdownDown from "../../../assets/images/dropdownDown.svg";
import StarIcon from "../../../assets/images/star.svg";
import DateRangePicker, { DateRange } from "rsuite/DateRangePicker";

type AdminFilterOverlayProps = {
  open: boolean;
  onClose: () => void;
  onApply: (filters: any) => void;
  userRoles?: string[];
  initialFilters?: any;
  displayFilters?: any;
  customClass?: string;
};

const AdminFilterOverlay = ({
  open,
  onClose,
  userRoles = [],
  onApply,
  initialFilters,

  displayFilters,
  customClass = "",
}: AdminFilterOverlayProps) => {
  // If you want to add role-based filters, do it here before setFilters
  const [filters, setFilters] = useState<any[]>(displayFilters);
  // State for each filter type
  const [selected, setSelected] = useState<{
    [key: string]: string | string[];
  }>(initialFilters || {});
  const [rangeOperator, setRangeOperator] = useState(">=");
  const [rangeValue, setRangeValue] = useState("");
  const [selectedLocation, setSelectedLocation] = useState<string[]>([]);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);
  // Dummy trigger for demonstration (replace with your form trigger if needed)
  // Dummy trigger for demonstration (replace with your form trigger if needed)
  const trigger = (_field?: string) => {};
  const clearDates = () => setDateRange([null, null]);

  // ...inside your component...

  const [placement, setPlacement] = useState<"bottomStart" | "topStart">(
    "bottomStart",
  );
  const [dateRange, setDateRange] = useState(null);

  const handleOpen = () => {
    if (calendarRef.current) {
      const rect = calendarRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Assume calendar height around 300px
      if (spaceBelow < 300 && spaceAbove > 300) {
        setPlacement("topStart");
      } else {
        setPlacement("bottomStart");
      }
    }

    setCalendarOpen(true);
  };

  // Handler for date range change
  const handleDateRangeChange = (value: DateRange | null) => {
    setDateRange(value);
  };
  useEffect(() => {
    if (initialFilters) {
      setSelected(initialFilters);
      // set other filter states if needed, e.g. range, dropdown, etc.

      // Prefill range values if present
      if (
        initialFilters.numberOfHdbs &&
        typeof initialFilters.numberOfHdbs === "object"
      ) {
        setRangeOperator(initialFilters.numberOfHdbs.operator || ">=");
        setRangeValue(initialFilters.numberOfHdbs.value || "");
      } else {
        setRangeOperator(">=");
        setRangeValue("");
      }

      // Prefill location if present
      if (initialFilters.location && Array.isArray(initialFilters.location)) {
        setSelectedLocation(initialFilters.location);
      } else {
        setSelectedLocation([]);
      }

      // Prefill date range if present
      if (
        initialFilters.dateRange &&
        initialFilters.dateRange.startDate &&
        initialFilters.dateRange.endDate
      ) {
        const parseDate = (str: string) => {
          const [day, month, year] = str.split("/");
          return new Date(Number(year), Number(month) - 1, Number(day));
        };
        setDateRange([
          parseDate(initialFilters.dateRange.startDate),
          parseDate(initialFilters.dateRange.endDate),
        ]);
      } else {
        setDateRange([null, null]);
      }
    } else {
      setSelected({});
      setRangeOperator(">=");
      setRangeValue("");
      setSelectedLocation([]);
      setDateRange([null, null]);
    }
  }, [initialFilters, open]);
  // Reset all filters
  const handleReset = () => {
    setSelected({});
    setRangeOperator(">=");
    setRangeValue("");
    setSelectedLocation([]);
    setDateRange([null, null]); // Reset date range
    
  };

  // Apply filters
  const handleApply = () => {
    const filterObj: any = {};

    Object.entries(selected).forEach(([key, value]) => {
      filterObj[key] = value;
    });

    // Range filter
    if (rangeValue) {
      filterObj.numberOfHdbs = {
        operator: rangeOperator,
        value: rangeValue,
      };
    }

    // Dropdown (location)
    if (selectedLocation.length > 0) {
      filterObj.location = selectedLocation;
    }

    // Date range filter
    if (dateRange && dateRange[0] && dateRange[1]) {
      // Convert Date objects to string format "dd/MM/yyyy"
      const formatDate = (date: Date) => {
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      };
      filterObj.dateRange = {
        startDate: formatDate(dateRange[0] as Date),
        endDate: formatDate(dateRange[1] as Date),
      };
    }
    console.log("filters", filterObj);
    if (onApply) onApply(filterObj);
    onClose();
  };

  // Handler for checkbox and radio
  const handleChange = (filterKey: string, value: string, type: string) => {
    setSelected((prev) => {
      const updated = { ...prev };
  
      if (type === "checkbox") {
        const prevArr = Array.isArray(prev[filterKey])
          ? (prev[filterKey] as string[])
          : [];
  
        const newArr = prevArr.includes(value)
          ? prevArr.filter((v) => v !== value)
          : [...prevArr, value];
  
        if (newArr.length === 0) {
          delete updated[filterKey];
        } else {
          updated[filterKey] = newArr;
        }
      } else {
        updated[filterKey] = value;
      }
  
      return updated;
    });
  };

  useEffect(() => {
    if (!calendarOpen) return;
    const handleScroll = () => {
      setCalendarOpen(false);
    };
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [calendarOpen]);

  return (
    <SideDrawerOverlay
      open={open}
      grayLine={true}
      onClose={onClose}
      headerText="Filters"
      customClass={customClass}
      footer={
        <>
          <MuiButton
            variant="outline"
            className={styles.cancelButton}
            onClick={handleReset}
            disabled={
              Object.keys(selected).length === 0 &&
              !rangeValue &&
              selectedLocation.length === 0 &&
              (!dateRange || !dateRange[0] || !dateRange[1])
            }
          >
            reset
          </MuiButton>
          <Button
            buttonTextClassName={styles.saveButtonText}
            buttonClassName={styles.saveButton}
            onClick={handleApply}
          >
            <span className={styles.saveButtonTextCenter}>apply</span>
          </Button>
        </>
      }
    >
      <div className={styles.filterContainer}>
        {filters.map((filter: any) => (
          <div key={filter.key} className={styles.filterRow}>
            <div className={styles.filterFieldName}>{filter.label}</div>
            <div className={styles.filterButtons}>
              {filter.type === "range" ? (
                <div className={styles.hdbFilterRow}>
                  <select
                    value={rangeOperator}
                    onChange={(e) => setRangeOperator(e.target.value)}
                    className={styles.hdbSelectWithIcon}
                  >
                    {filter.operators.map((option: any) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    min={0}
                    value={rangeValue}
                    onChange={(e) =>
                      setRangeValue(e.target.value.replace(/[^0-9]/g, ""))
                    }
                    className={styles.hdbInputFlex}
                  />
                </div>
              ) : filter.type === "date" ? (
                <div className={styles.datePickerWrapper} ref={calendarRef}>
                  <DateRangePicker
                    placement={placement}
                    value={dateRange}
                    onChange={(value) => {
                      handleDateRangeChange(value);
                      trigger("startDate");
                      trigger("endDate");
                    }}
                    ranges={[]}
                    format="dd/MM/yyyy"
                    onClean={() => setDateRange(null)}
                    placeholder="Select program date range"
                    style={{ width: "100%" }}
                    className={`${styles.datePicker} ${styles.datePickerGrayBorder}`}
                    onBlur={() => {
                      trigger("startDate");
                      trigger("endDate");
                    }}
                    onOpen={handleOpen}
                    onClose={() => setCalendarOpen(false)}
                    open={calendarOpen}
                  />
                </div>
              ) : filter.type === "dropdown" ? (
                <MultiSelect
                  options={filter.options.map((l: any) => l.label)}
                  selected={selectedLocation}
                  setSelected={setSelectedLocation}
                  closeOnClickOutside={true} // Add this prop if supported
                />
              ) : (
                filter.options.map((option: any) => (
                  <label key={option.value} className={styles.filterButton}>
                    <input
                      type={filter.type}
                      name={filter.key}
                      checked={
                        filter.type === "checkbox"
                          ? (
                              selected[filter.key] as string[] | undefined
                            )?.includes(option.value) || false
                          : selected[filter.key] === option.value
                      }
                      value={option.value}
                      onChange={() =>
                        handleChange(filter.key, option.value, filter.type)
                      }
                      style={{ display: "none" }}
                    />
                    <img
                      src={
                        filter.type === "checkbox"
                          ? (
                              selected[filter.key] as string[] | undefined
                            )?.includes(option.value)
                            ? CheckBoxChecked
                            : CheckBoxUnchecked
                          : selected[filter.key] === option.value
                            ? "RadioChecked"
                            : RadioUnchecked
                      }
                      alt={
                        filter.type === "checkbox"
                          ? (
                              selected[filter.key] as string[] | undefined
                            )?.includes(option.value)
                            ? "Checked"
                            : "Unchecked"
                          : selected[filter.key] === option.value
                            ? "Checked"
                            : "Unchecked"
                      }
                      className={
                        filter.type === "checkbox"
                          ? styles.customCheckbox
                          : styles.customRadio
                      }
                    />
                    {filter.type === "checkbox" &&
                      filter.label === "RM rating" && option.label !== "Yet to review" && (
                        <img
                          src={StarIcon}
                          alt="star"
                          className={styles.starIcon}
                        />
                      )}
                    {option.label}
                  </label>
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    </SideDrawerOverlay>
  );
};

export default AdminFilterOverlay;
